/**
 * CatchballExchangeTimeline
 *
 * Renders the complete bidirectional exchange for a Catchball loop as a
 * chronological conversation — the "exchange is the artifact" view.
 *
 * Shows: intent (down) → team proposal (up) → executive counter (down) →
 *        acceptance (up) — all on one screen with actor, date, and content.
 *
 * Read-only. No mutations. Works with both closed and in-progress loops.
 */

import { useMemo } from 'react';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle2,
  Clock,
  FileText,
  MessageSquare,
  AlertCircle,
} from 'lucide-react';
import type { CatchballLoop, LoopIntent, LoopSynthesis } from '../../../types/prioritization/catchballLoop';
import type { CatchballProposal } from '../../../types/prioritization/catchballProposal';
import { LoopDiffBlock } from './LoopDiffBlock';

// ─── Types ────────────────────────────────────────────────────────────────────

type ExchangeDirection = 'down' | 'up';

interface ExchangeEntry {
  id: string;
  direction: ExchangeDirection;
  actorName?: string;
  actorRole?: string;
  timestamp?: Date;
  type: 'intent' | 'proposal' | 'counter' | 'acceptance' | 'synthesis';
  /** The proposal behind an upward entry, so the pass can be compared with the one before it. */
  proposalId?: string;
  status?:
    | 'pending'
    | 'acknowledged'
    | 'draft'
    | 'proposed'
    | 'returned'
    | 'accepted'
    | 'accepted_provisional'
    | 'countered'
    | 'rejected'
    | 'withdrawn';
  // Content
  strategicContext?: string;
  asks?: string[];
  constraints?: string[];
  rationale?: string;
  summary?: string;
  recommendations?: string[];
  label: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(date?: Date): string {
  if (!date) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Smart text renderer. Handles three cases in priority order:
 * 1. Parenthetical numbered list: "(1) ... (2) ..." → pill-numbered list
 * 2. Dot-prefixed numbered list:  "1. ... 2. ..."   → pill-numbered list
 * 3. Plain prose: splits on double-newlines into paragraphs.
 */
function FormattedText({ text, className = '' }: { text: string; className?: string }) {
  // Case 1: parenthetical numbered items — "(1) text (2) text ..."
  const parenMatches = [...text.matchAll(/\((\d+)\)/g)];
  if (parenMatches.length >= 2) {
    const firstIdx = text.indexOf('(1)');
    const intro = firstIdx > 0 ? text.slice(0, firstIdx).trim() : null;
    const listText = firstIdx >= 0 ? text.slice(firstIdx) : text;
    const items = listText.split(/\(\d+\)\s*/).filter(s => s.trim().length > 0);
    return (
      <div className={`space-y-2 ${className}`}>
        {intro && <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">{intro}</p>}
        <ol className="space-y-2.5 list-none">
          {items.map((item, i) => (
            <li key={i} className="flex gap-2.5 items-start">
              <span className="shrink-0 mt-0.5 h-5 w-5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] text-xs font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
                {item.trim().replace(/\s*\.$/, '')}
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  // Case 2: "1. text\n2. text ..."
  if (/^\d+\.\s+/m.test(text)) {
    const items = text.split(/\n?\d+\.\s+/).filter(s => s.trim().length > 0);
    return (
      <ol className={`space-y-2.5 list-none ${className}`}>
        {items.map((item, i) => (
          <li key={i} className="flex gap-2.5 items-start">
            <span className="shrink-0 mt-0.5 h-5 w-5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] text-xs font-semibold flex items-center justify-center">
              {i + 1}
            </span>
            <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">{item.trim()}</span>
          </li>
        ))}
      </ol>
    );
  }

  // Case 3: plain prose
  const paragraphs = text.split(/\n\n+/).filter(s => s.trim().length > 0);
  return (
    <div className={`space-y-2 ${className}`}>
      {paragraphs.map((p, i) => (
        <p key={i} className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed whitespace-pre-line">
          {p.trim()}
        </p>
      ))}
    </div>
  );
}

function statusChip(status?: ExchangeEntry['status']) {
  switch (status) {
    case 'accepted':
      return { label: 'Accepted', cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' };
    case 'countered':
      return { label: 'Countered', cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' };
    case 'rejected':
      return { label: 'Rejected', cls: 'bg-[var(--coral-dim)] text-[var(--coral)]' };
    case 'pending':
    case 'proposed':
      return { label: 'Awaiting decision', cls: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' };
    case 'returned':
      return { label: 'Returned', cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' };
    case 'accepted_provisional':
      return { label: 'Accepted with conditions', cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' };
    case 'acknowledged':
      return { label: 'Acknowledged', cls: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' };
    default:
      return null;
  }
}

// ─── Entry card ───────────────────────────────────────────────────────────────

function EntryCard({ entry, isLast, loopId }: { entry: ExchangeEntry; isLast: boolean; loopId: string }) {
  const isDown = entry.direction === 'down';
  // Suppress the status chip on counter entries — a status of "accepted" on a
  // counter means the counter-direction was incorporated, not that it was the
  // exchange's acceptance event. Showing it confuses the artifact.
  const chip = entry.type === 'counter' ? null : statusChip(entry.status);

  const accent = isDown
    ? {
        border: 'border-primary-200',
        bg: 'bg-primary-50',
        icon: 'text-primary-600',
        label: 'text-primary-700',
        dot: 'bg-primary-400',
        line: 'bg-primary-200',
      }
    : {
        border: 'border-[var(--slate)]',
        bg: 'bg-[var(--slate-dim)]',
        icon: 'text-[var(--text-ter)]',
        label: 'text-[var(--text-sec)]',
        dot: 'bg-[var(--slate)]',
        line: 'bg-[var(--slate-dim)]',
      };

  if (entry.type === 'acceptance') {
    // Special accepted styling
    return (
      <div className="flex gap-4">
        <div className="flex flex-col items-center flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-[var(--green-dim)] border-2 border-[var(--green)] flex items-center justify-center">
            <CheckCircle2 className="h-4 w-4 text-[var(--green)]" />
          </div>
          {!isLast && <div className="w-0.5 flex-1 mt-2 bg-[var(--hover-tint)]" />}
        </div>
        <div className={`flex-1 mb-6  border border-[var(--green)] bg-[var(--green-dim)] p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--green)] dark:text-[var(--text-ter)]">
                {entry.label}
              </span>
              {entry.actorName && (
                <span className="ml-2 text-xs text-[var(--text-ter)]">{entry.actorName}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {entry.timestamp && (
                <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
                  <Clock className="h-3 w-3" />
                  {fmt(entry.timestamp)}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
                <CheckCircle2 className="h-3 w-3" />
                Accepted
              </span>
            </div>
          </div>
          {entry.rationale && <FormattedText text={entry.rationale} />}
          {entry.summary && <FormattedText text={entry.summary} />}
          {entry.proposalId && (
            <LoopDiffBlock loopId={loopId} proposalId={entry.proposalId} variant="timeline" className="mt-3" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      {/* Timeline spine */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`w-8 h-8 rounded-full border-2 ${accent.border} ${accent.bg} flex items-center justify-center`}>
          {isDown
            ? <ArrowDownCircle className={`h-4 w-4 ${accent.icon}`} />
            : <ArrowUpCircle className={`h-4 w-4 ${accent.icon}`} />
          }
        </div>
        {!isLast && <div className={`w-0.5 flex-1 mt-2 ${accent.line}`} />}
      </div>

      {/* Card */}
      <div className={`flex-1 mb-6  border ${accent.border} ${accent.bg} p-4`}>
        {/* Header row */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <span className={`text-xs font-semibold uppercase tracking-wider ${accent.label}`}>
              {entry.label}
            </span>
            {entry.actorName && (
              <span className="ml-2 text-xs font-medium text-[var(--text-sec)]">{entry.actorName}</span>
            )}
            {entry.actorRole && (
              <span className="ml-1 text-xs text-[var(--text-ter)]">· {entry.actorRole}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {entry.timestamp && (
              <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
                <Clock className="h-3 w-3" />
                {fmt(entry.timestamp)}
              </span>
            )}
            {chip && (
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${chip.cls}`}>
                {chip.label}
              </span>
            )}
          </div>
        </div>

        {/* Strategic context */}
        {entry.strategicContext && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">Context</p>
            <FormattedText text={entry.strategicContext} />
          </div>
        )}

        {/* Asks */}
        {entry.asks && entry.asks.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">
              {isDown ? 'What was asked' : 'Response'}
            </p>
            <ul className="space-y-1.5">
              {entry.asks.map((ask, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-sec)]">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--text-ter)] flex-shrink-0" />
                  <FormattedText text={ask} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Constraints */}
        {entry.constraints && entry.constraints.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">Constraints</p>
            <ul className="space-y-1.5">
              {entry.constraints.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Rationale */}
        {entry.rationale && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">Rationale</p>
            <FormattedText text={entry.rationale} />
          </div>
        )}

        {/* Summary / recommendations */}
        {entry.summary && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">Summary</p>
            <FormattedText text={entry.summary} />
          </div>
        )}
        {entry.recommendations && entry.recommendations.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">Recommendations</p>
            <ul className="space-y-1.5">
              {entry.recommendations.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-sec)]">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--text-ter)] flex-shrink-0" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* What changed since the pass before this one (roadmap C-1). */}
        {entry.proposalId && (
          <LoopDiffBlock loopId={loopId} proposalId={entry.proposalId} variant="timeline" className="mt-3" />
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface CatchballExchangeTimelineProps {
  loop: CatchballLoop;
  proposals: CatchballProposal[];
  cycleOwnerName?: string;
}

export function CatchballExchangeTimeline({
  loop,
  proposals,
  cycleOwnerName,
}: CatchballExchangeTimelineProps) {
  const entries = useMemo<ExchangeEntry[]>(() => {
    const result: ExchangeEntry[] = [];

    // 1. Intent (executive ask, direction: down)
    if (loop.intent) {
      const intent: LoopIntent = loop.intent;
      result.push({
        id: 'intent',
        direction: 'down',
        actorName: cycleOwnerName,
        actorRole: intent.evaluatorLevel ?? 'Executive',
        timestamp: loop.requestIssuedAt,
        type: 'intent',
        label: 'Request sent down',
        strategicContext: intent.strategicContext,
        asks: intent.asks,
        constraints: intent.constraints,
      });
    }

    // 2. Proposals sorted chronologically
    const sortedProposals = [...proposals].sort(
      (a, b) => new Date(a.proposedAt).getTime() - new Date(b.proposedAt).getTime(),
    );

    for (const p of sortedProposals) {
      const isUp = p.direction === 'up';
      const isCounter = !isUp;
      // Acceptance = team's UP proposal that the executive accepted.
      // A DOWN counter-proposal with status='accepted' means the counter was
      // incorporated, not that it was the acceptance event in the exchange.
      const isAcceptance = isUp && (
        p.status === 'accepted' ||
        p.resolution === 'accepted_as_is' ||
        p.resolution === 'accepted_with_changes'
      );

      result.push({
        id: p.id,
        direction: p.direction,
        actorName: p.proposedByName ?? p.proposedBy,
        actorRole: p.proposedByOrgUnitName,
        timestamp: new Date(p.proposedAt),
        type: isAcceptance ? 'acceptance' : isCounter ? 'counter' : 'proposal',
        status: p.status,
        label: isAcceptance
          ? 'Accepted'
          : isCounter
          ? 'Counter-proposal sent down'
          : 'Proposal returned up',
        rationale: p.rationale,
        proposalId: isUp ? p.id : undefined,
      });
    }

    // 3. Synthesis (if present and no proposal shows acceptance)
    const synthesis: LoopSynthesis | undefined = loop.synthesis;
    const alreadyShowsAccepted = result.some(e => e.type === 'acceptance');
    if (synthesis && !alreadyShowsAccepted) {
      result.push({
        id: 'synthesis',
        direction: 'up',
        actorName: synthesis.synthesizedBy,
        timestamp: synthesis.synthesizedAt,
        type: 'synthesis',
        label: 'Team synthesis',
        summary: synthesis.summary,
        recommendations: synthesis.recommendations,
      });
    }

    return result;
  }, [loop, proposals, cycleOwnerName]);

  if (entries.length === 0) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-6 text-center">
        <MessageSquare className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
        <p className="text-sm text-[var(--text-ter)]">No exchange on record yet. The exchange will appear here once the ball is in play.</p>
      </div>
    );
  }

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
      <div className="flex items-center gap-2 mb-5">
        <FileText className="h-4 w-4 text-[var(--text-ter)]" />
        <h2 className="text-base font-semibold text-[var(--text-pri)]">Exchange record</h2>
        <span className="ml-auto text-xs text-[var(--text-ter)] font-medium">
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'} on record
        </span>
      </div>

      <div className="relative">
        {entries.map((entry, idx) => (
          <EntryCard key={entry.id} entry={entry} isLast={idx === entries.length - 1} loopId={loop.id} />
        ))}
      </div>

      <p className="mt-2 text-xs text-[var(--text-ter)] border-t border-[var(--rule)] pt-3">
        This record is immutable. Amendments appear as new entries, not edits to prior ones.
      </p>
    </div>
  );
}
